import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "../theme";
import TimelinePlanner from "./TimelinePlanner";
import { FiUser, FiClock, FiDollarSign, FiMapPin, FiInfo } from "react-icons/fi";

const PlannerWrapper = styled.div`
  padding: 1rem 0 2rem 0;
  max-width: 100%;
`;

const Banner = styled.div`
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
  color: white;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  h2 {
    margin: 0 0 2px 0;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    line-height: 1.3;
    
    @media (max-width: 768px) {
      font-size: 1rem;
      flex-wrap: wrap;
      gap: 0.25rem 0.5rem;
    }
  }
  
  .divider {
    opacity: 0.7;
    font-weight: 300;
    @media (max-width: 768px) {
      display: inline;
    }
  }
  
  .promo-text {
    font-size: 0.75rem;
    opacity: 0.9;
    margin-top: 0.2rem;
    display: block;
    font-style: italic;
    line-height: 1.3;
    
    @media (max-width: 480px) {
      font-size: 0.7rem;
    }
  }
  
  .highlight {
    font-weight: 600;
    color: #FFD700;
    text-shadow: 0 0 2px rgba(0,0,0,0.3);
  }
`;

const PrivateTourBadge = styled.div`
  display: flex;
  align-items: center;
  background: #E31B23;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 8px;
  
  svg {
    margin-right: 4px;
  }
`;

const TourDetail = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: ${colors.textSecondary};
  
  svg {
    margin-right: 8px;
    color: ${colors.accent};
  }
`;

function loadDays() {
  try {
    const data = localStorage.getItem("itinerary_days");
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveDays(days) {
  localStorage.setItem("itinerary_days", JSON.stringify(days));
}

function convertLegacyDays(days) {
  return days.map(day => ({
    label: day.label,
    items: day.items.map(item => ({ title: item.title, time: null })),
  }));
}

export default function Planner({ days, onDaysChange: setDays, activitiesByCity, startDate }) {
  // Calculate total activities
  const totalActivities = days.reduce((total, day) => {
    if (day.slots) {
      return total + Object.values(day.slots).flat().length;
    }
    return total;
  }, 0);

  const handleAddDay = (template) => {
    let newDay;
    
    if (template.isPrivateTour) {
      // Handle private tour template
      newDay = {
        id: Date.now(),
        date: new Date(startDate.getTime() + days.length * 24 * 60 * 60 * 1000),
        city: template.city || 'Lisbon',
        slots: { ...template.slots },
        label: template.label || `Day ${days.length + 1}`,
        isPrivateTour: true
      };
      
      // Process slots for private tours
      Object.keys(newDay.slots).forEach(slotKey => {
        newDay.slots[slotKey] = newDay.slots[slotKey].map(activity => ({
          ...activity,
          isPrivate: true,
          pois: activity.pois || [],
          display: {
            ...(activity.display || {}),
            badge: 'Private Tour',
            highlights: activity.highlights || [],
            duration: activity.duration,
            price: activity.price,
            contactInfo: activity.contactInfo || {
              phone: '+351 123 456 789',
              email: 'info@portugaltours.com'
            }
          }
        }));
      });
    } else {
      // Handle regular template
      newDay = {
        id: Date.now(),
        date: new Date(startDate.getTime() + days.length * 24 * 60 * 60 * 1000),
        city: template.city || 'Lisbon',
        slots: {
          ...template.slots,
          allDay: (template.slots.allDay || []).map(activity => ({
            ...activity,
            isPrivate: false,
            pois: activity.pois || [],
            display: activity.display || null
          }))
        },
        label: template.label || `Day ${days.length + 1}`,
        isPrivateTour: false
      };
    }

    const updatedDays = [...days, newDay];
    setDays(updatedDays);
    saveDays(updatedDays);
  };

  return (
    <PlannerWrapper>
      <Banner>
        <h2>
          <span>{days.length} {days.length === 1 ? 'Day' : 'Days'}</span>
          <span className="divider">•</span>
          <span>{totalActivities} {totalActivities === 1 ? 'Activity' : 'Activities'}</span>
          <span className="divider">•</span>
          <span>Portugal Trip</span>
        </h2>
        <span className="promo-text">
          {totalActivities > 0 
            ? <><span className="highlight">Pro tip:</span> Drag and drop to reorder activities for the perfect schedule</>
            : <><span className="highlight">Plan with ease:</span> Add activities from the Map or Discover tab to begin</>
          }
        </span>
      </Banner>
      <TimelinePlanner
        days={days}
        setDays={setDays}
        activitiesByCity={activitiesByCity}
        startDate={startDate}
        renderActivity={(activity) => {
          const isPrivate = activity.isPrivate || activity.display?.badge === 'Private Tour';
          
          return (
            <div>
              {isPrivate && (
                <PrivateTourBadge>
                  <FiUser size={14} />
                  <span>{activity.display?.badge || 'Private Tour'}</span>
                </PrivateTourBadge>
              )}
              
              {/* Display tour highlights if available */}
              {activity.display?.highlights?.length > 0 && (
                <div style={{ margin: '8px 0' }}>
                  <strong>Tour Highlights:</strong>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {activity.display.highlights.map((highlight, i) => (
                      <li key={i} style={{ fontSize: '0.9em', marginBottom: '4px' }}>{highlight}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Display tour details */}
              <div style={{ marginTop: '8px' }}>
                {(activity.duration || activity.display?.duration) && (
                  <TourDetail>
                    <FiClock size={14} />
                    <span>{activity.duration || activity.display?.duration}</span>
                  </TourDetail>
                )}
                
                {(activity.price || activity.display?.price) && (
                  <TourDetail>
                    <FiDollarSign size={14} />
                    <span>{activity.price || activity.display?.price}</span>
                  </TourDetail>
                )}
                
                {(activity.meetingPoint || activity.display?.meetingPoint) && (
                  <TourDetail>
                    <FiMapPin size={14} />
                    <span>Meet: {activity.meetingPoint || activity.display?.meetingPoint}</span>
                  </TourDetail>
                )}
                
                {activity.display?.contactInfo?.phone && (
                  <TourDetail>
                    <FiInfo size={14} />
                    <span>Contact: {activity.display.contactInfo.phone}</span>
                  </TourDetail>
                )}
                
                {activity.pois && activity.pois.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h4 style={{ 
                      fontSize: '0.95rem', 
                      color: colors.primary,
                      marginBottom: '0.5rem'
                    }}>
                      Tour Stops:
                    </h4>
                    <ul style={{ 
                      paddingLeft: '1.5rem',
                      listStyleType: 'none'
                    }}>
                      {activity.pois.map((poi, index) => (
                        <li key={index} style={{ 
                          marginBottom: '0.5rem',
                          position: 'relative',
                          paddingLeft: '1rem'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            color: colors.accent
                          }}>•</span>
                          <strong>{poi.name}</strong> ({poi.duration}) — {poi.highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        }}
      />
    </PlannerWrapper>
  );
}
